import { useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import PropTypes from "prop-types";
import { alpha, useTheme } from "@mui/material/styles";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import MDSnackbar from "components/MDSnackbar";
import MDButton from "components/MDButton";
import { store } from "../redux/store";
import {
  GetCurrentUserProfileCall,
  UpdateCurrentUserProfileCall,
  UploadCurrentUserProfileImageCall,
} from "../redux/api/profile";

function AppProfileMenu({ light, variant, collapsed }) {
  const theme = useTheme();
  const [auth, setAuth] = useState(() => store.getState().auth || {});
  const fileInputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    color: "success",
    title: "",
    content: "",
  });
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    title: "",
    organization: "",
    about: "",
  });

  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      setAuth(store.getState().auth || {});
    });
    return unsubscribe;
  }, []);

  const displayName = useMemo(() => {
    const fullName = [auth?.first_name, auth?.last_name].filter(Boolean).join(" ").trim();
    return fullName || auth?.email || "My profile";
  }, [auth?.email, auth?.first_name, auth?.last_name]);

  const email = auth?.email || "No email available";
  const avatarSrc = auth?.profile_image || "";
  const isSidenav = variant === "sidenav";
  const initials = useMemo(() => {
    const source = [auth?.first_name, auth?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    if (source) {
      return source
        .split(" ")
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join("");
    }

    return (auth?.email || "U").slice(0, 1).toUpperCase();
  }, [auth?.email, auth?.first_name, auth?.last_name]);

  const syncFormFromAuth = () => {
    setForm({
      first_name: auth?.first_name || "",
      last_name: auth?.last_name || "",
      email: auth?.email || "",
      title: auth?.title || "",
      organization: auth?.organization || "",
      about: auth?.about || "",
    });
  };

  useEffect(() => {
    if (open) {
      syncFormFromAuth();
    }
  }, [open, auth?.about, auth?.email, auth?.first_name, auth?.last_name, auth?.organization, auth?.title]);

  const handleOpen = async () => {
    setOpen(true);
    await GetCurrentUserProfileCall();
  };

  const handleClose = () => {
    if (saving || uploading) return;
    setOpen(false);
  };

  const handleChange = (key) => (event) => {
    setForm((prev) => ({
      ...prev,
      [key]: event.target.value,
    }));
  };

  const showSnackbar = (color, title, content) => {
    setSnackbar({
      open: true,
      color,
      title,
      content,
    });
  };

  const handleSave = async () => {
    setSaving(true);
    const response = await UpdateCurrentUserProfileCall({
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      title: form.title.trim(),
      organization: form.organization.trim(),
      about: form.about.trim(),
    });
    setSaving(false);

    if (response?.id) {
      showSnackbar("success", "Profile updated", "Your profile details were saved.");
      setOpen(false);
      return;
    }

    showSnackbar("error", "Update failed", "We could not save your profile right now.");
  };

  const handlePickImage = () => {
    fileInputRef.current?.click();
  };

  const handleUploadImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    setUploading(true);
    const response = await UploadCurrentUserProfileImageCall(file);
    setUploading(false);

    if (response?.success) {
      showSnackbar("success", "Photo updated", "Your profile image has been updated.");
      return;
    }

    showSnackbar("error", "Upload failed", "We could not upload your profile picture.");
  };

  const triggerSx = isSidenav
    ? {
        width: "100%",
        justifyContent: collapsed ? "center" : "flex-start",
        borderRadius: 3,
        px: collapsed ? 0.75 : 1.25,
        py: 1,
        gap: collapsed ? 0 : 1.25,
        background: "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,255,255,0.08))",
        border: "1px solid rgba(255,255,255,0.12)",
        boxShadow: "0 14px 30px rgba(15,23,42,0.14)",
        backdropFilter: "blur(18px)",
        transition: "all 0.18s ease",
        "&:hover": {
          background: "linear-gradient(135deg, rgba(255,255,255,0.22), rgba(255,255,255,0.12))",
          transform: "translateY(-1px)",
        },
      }
    : {
        borderRadius: 999,
        px: { xs: 0.5, sm: 1 },
        py: 0.5,
        gap: 1,
        maxWidth: { xs: 44, sm: 280 },
        background: alpha(light ? "#ffffff" : theme.palette.common.white, light ? 0.18 : 0.7),
        border: `1px solid ${alpha(light ? "#ffffff" : theme.palette.text.primary, light ? 0.22 : 0.08)}`,
        backdropFilter: "blur(16px)",
        boxShadow: `0 10px 30px ${alpha(theme.palette.common.black, 0.08)}`,
        transition: "all 0.18s ease",
        "&:hover": {
          background: alpha(light ? "#ffffff" : theme.palette.common.white, light ? 0.24 : 0.88),
          transform: "translateY(-1px)",
        },
      };

  return (
    <>
      <ButtonBase
        onClick={handleOpen}
        sx={triggerSx}
      >
        <Avatar
          src={avatarSrc}
          alt={displayName}
          sx={{
            width: isSidenav ? 38 : 34,
            height: isSidenav ? 38 : 34,
            fontSize: isSidenav ? 14 : 13,
            fontWeight: 700,
            background: "linear-gradient(135deg, #38bdf8 0%, #6366f1 100%)",
          }}
        >
          {initials}
        </Avatar>
        <Box
          sx={{
            display: isSidenav ? (collapsed ? "none" : "block") : { xs: "none", sm: "block" },
            textAlign: "left",
            minWidth: 0,
            flex: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: isSidenav ? "0.84rem" : "0.82rem",
              fontWeight: 700,
              lineHeight: 1.15,
              color: isSidenav ? "#ffffff" : light ? "#ffffff" : "#0f172a",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: isSidenav ? "100%" : 170,
            }}
          >
            {displayName}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.72rem",
              lineHeight: 1.15,
              color: isSidenav ? "rgba(255,255,255,0.72)" : light ? alpha("#ffffff", 0.84) : "#64748b",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: isSidenav ? "100%" : 170,
            }}
          >
            {email}
          </Typography>
        </Box>
        <ExpandMoreRoundedIcon
          sx={{
            display: isSidenav ? (collapsed ? "none" : "block") : { xs: "none", sm: "block" },
            fontSize: 18,
            color: isSidenav ? "rgba(255,255,255,0.86)" : light ? "#ffffff" : "#475569",
          }}
        />
      </ButtonBase>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            backgroundImage:
              "linear-gradient(180deg, rgba(248,250,252,0.98) 0%, rgba(255,255,255,0.98) 100%)",
            boxShadow: "0 35px 90px rgba(15, 23, 42, 0.24)",
          },
        }}
      >
        <Box
          sx={{
            px: { xs: 2.5, sm: 3 },
            pt: 2.5,
            pb: 2,
            background:
              "radial-gradient(circle at top left, rgba(56,189,248,0.24), transparent 36%), linear-gradient(135deg, rgba(14,165,233,0.12), rgba(99,102,241,0.14))",
            borderBottom: "1px solid rgba(148,163,184,0.18)",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ position: "relative" }}>
                <Avatar
                  src={avatarSrc}
                  alt={displayName}
                  sx={{
                    width: 84,
                    height: 84,
                    fontSize: 28,
                    fontWeight: 700,
                    border: "3px solid rgba(255,255,255,0.75)",
                    boxShadow: "0 18px 32px rgba(15, 23, 42, 0.16)",
                    background: "linear-gradient(135deg, #38bdf8 0%, #6366f1 100%)",
                  }}
                >
                  {initials}
                </Avatar>
                <IconButton
                  onClick={handlePickImage}
                  disabled={uploading}
                  sx={{
                    position: "absolute",
                    right: -4,
                    bottom: -4,
                    width: 34,
                    height: 34,
                    background: "#0f172a",
                    color: "#ffffff",
                    boxShadow: "0 12px 24px rgba(15, 23, 42, 0.2)",
                    "&:hover": {
                      background: "#1e293b",
                    },
                  }}
                >
                  {uploading ? <CircularProgress size={16} sx={{ color: "#ffffff" }} /> : <CameraAltOutlinedIcon sx={{ fontSize: 18 }} />}
                </IconButton>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleUploadImage}
                />
              </Box>
              <Box>
                <Chip
                  label="My profile"
                  size="small"
                  sx={{
                    mb: 1,
                    fontWeight: 700,
                    color: "#0f172a",
                    backgroundColor: "rgba(255,255,255,0.85)",
                  }}
                />
                <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: "#0f172a" }}>
                  {displayName}
                </Typography>
                <Typography sx={{ fontSize: "0.88rem", color: "#475569" }}>{email}</Typography>
                <Typography sx={{ mt: 0.5, fontSize: "0.75rem", color: "#64748b" }}>
                  Tap the camera button to update your profile picture.
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={handleClose} disabled={saving || uploading}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </Box>

        <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, py: 3 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <TextField
              label="First name"
              value={form.first_name}
              onChange={handleChange("first_name")}
              InputProps={{
                startAdornment: <PersonOutlineRoundedIcon sx={{ mr: 1, color: "#94a3b8" }} />,
              }}
            />
            <TextField
              label="Last name"
              value={form.last_name}
              onChange={handleChange("last_name")}
              InputProps={{
                startAdornment: <BadgeRoundedIcon sx={{ mr: 1, color: "#94a3b8" }} />,
              }}
            />
            <TextField
              label="Email"
              value={form.email}
              disabled
              InputProps={{
                startAdornment: <MailOutlineRoundedIcon sx={{ mr: 1, color: "#94a3b8" }} />,
              }}
              sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
            />
            <TextField
              label="Title"
              value={form.title}
              onChange={handleChange("title")}
              placeholder="Professor, Admin, Researcher..."
              InputProps={{
                startAdornment: <BadgeRoundedIcon sx={{ mr: 1, color: "#94a3b8" }} />,
              }}
            />
            <TextField
              label="Organization"
              value={form.organization}
              onChange={handleChange("organization")}
              InputProps={{
                startAdornment: <BusinessRoundedIcon sx={{ mr: 1, color: "#94a3b8" }} />,
              }}
            />
            <TextField
              label="About"
              value={form.about}
              onChange={handleChange("about")}
              multiline
              minRows={4}
              placeholder="Add a short bio or teaching focus..."
              sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
            />
          </Box>
          <Typography sx={{ mt: 1.25, fontSize: "0.75rem", color: "#64748b" }}>
            Email is read-only here and stays linked to the current login account.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2.5, sm: 3 }, pb: 3, pt: 0 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={handleClose}
            disabled={saving || uploading}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleSave}
            disabled={saving || uploading}
          >
            {saving ? "Saving..." : "Save changes"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <MDSnackbar
        color={snackbar.color}
        icon={snackbar.color === "success" ? "check" : "warning"}
        title={snackbar.title}
        content={snackbar.content}
        dateTime=""
        open={snackbar.open}
        close={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        bgWhite
      />
    </>
  );
}

AppProfileMenu.defaultProps = {
  light: false,
  variant: "topbar",
  collapsed: false,
};

AppProfileMenu.propTypes = {
  light: PropTypes.bool,
  variant: PropTypes.oneOf(["topbar", "sidenav"]),
  collapsed: PropTypes.bool,
};

export default AppProfileMenu;
